import type { FilterGroup } from '~/models/Filter.models';
import {
  formatSelectedFilterValueForQuery,
  readFilterQueryValueForFilterGroup,
} from './filterQueryParams';

const PLAN_STRING_3_GB = '3 GB';
const PLAN_STRING_5_GB = '5 GB';
const PLAN_STRING_8_GB = '8 GB';
const PLAN_STRING_30_GB = '30 GB';
const PLAN_STRING_100_GB = '100 GB';
const PLAN_STRING_STUDENT_30_GB = 'Student 30 GB';
const PLAN_STRING_STUDENT_100_GB = 'Student 100 GB';

const PLAN_MAP_KEY_3_GB = '3';
const PLAN_MAP_KEY_5_GB = '5';
const PLAN_MAP_KEY_8_GB = '8';
const PLAN_MAP_KEY_30_GB = '30';
const PLAN_MAP_KEY_100_GB = '100';
const PLAN_MAP_KEY_STUDENT_30_GB = 'student30';
const PLAN_MAP_KEY_STUDENT_100_GB = 'student100';

const PARTIAL_PAYMENT_STRING_DIRECT = 'Direktbetalning';
const PARTIAL_PAYMENT_STRING_12 = '12 mån';
const PARTIAL_PAYMENT_STRING_24 = '24 mån';
const PARTIAL_PAYMENT_STRING_36 = '36 mån';

export const createPhoneFilterOptions = (
  uniqueManufacturerNames: string[],
  isUsed = false,
  defaultPlanId: number,
  defaultDownPayment: number,
): FilterGroup[] => {
  const planStore = usePlanStore();

  const queryVendorValues =
    readFilterQueryValueForFilterGroup('Tillverkare').split(',');
  const queryPlanValue = readFilterQueryValueForFilterGroup('Abonnemang');
  const queryPartialPaymentValue =
    readFilterQueryValueForFilterGroup('Delbetalning');

  const featuredRow = {
    Apple: true,
    Samsung: true,
    Google: true,
  };

  const plansFilters = [
    {
      filterId: useId(),
      name: PLAN_STRING_8_GB,
      default: queryPlanValue
        ? formatSelectedFilterValueForQuery(PLAN_STRING_8_GB) === queryPlanValue
        : defaultPlanId ===
          planStore.planAllowanceToPlanIdMapForFilter[PLAN_MAP_KEY_8_GB],
    },
    {
      filterId: useId(),
      name: PLAN_STRING_30_GB,
      default: queryPlanValue
        ? formatSelectedFilterValueForQuery(PLAN_STRING_30_GB) ===
          queryPlanValue
        : defaultPlanId ===
          planStore.planAllowanceToPlanIdMapForFilter[PLAN_MAP_KEY_30_GB],
    },
    {
      filterId: useId(),
      name: PLAN_STRING_100_GB,
      default: queryPlanValue
        ? formatSelectedFilterValueForQuery(PLAN_STRING_100_GB) ===
          queryPlanValue
        : defaultPlanId ===
          planStore.planAllowanceToPlanIdMapForFilter[PLAN_MAP_KEY_100_GB],
    },
    {
      filterId: useId(),
      name: PLAN_STRING_STUDENT_30_GB,
      isAlternative: true,
      default: queryPlanValue
        ? formatSelectedFilterValueForQuery(PLAN_STRING_STUDENT_30_GB) ===
          queryPlanValue
        : defaultPlanId ===
          planStore.planAllowanceToPlanIdMapForFilter[
            PLAN_MAP_KEY_STUDENT_30_GB
          ],
    },
    {
      filterId: useId(),
      name: PLAN_STRING_STUDENT_100_GB,
      isAlternative: true,
      default: queryPlanValue
        ? formatSelectedFilterValueForQuery(PLAN_STRING_STUDENT_100_GB) ===
          queryPlanValue
        : defaultPlanId ===
          planStore.planAllowanceToPlanIdMapForFilter[
            PLAN_MAP_KEY_STUDENT_100_GB
          ],
    },
  ];

  const dataHas3GBPlan =
    !!planStore.planAllowanceToPlanIdMapForFilter[PLAN_MAP_KEY_3_GB];
  const dataHas5GBPlan =
    !!planStore.planAllowanceToPlanIdMapForFilter[PLAN_MAP_KEY_5_GB];

  if (dataHas3GBPlan && !dataHas5GBPlan) {
    plansFilters.unshift({
      filterId: useId(),
      name: PLAN_STRING_3_GB,
      default: queryPlanValue
        ? formatSelectedFilterValueForQuery(PLAN_STRING_3_GB) === queryPlanValue
        : defaultPlanId ===
          planStore.planAllowanceToPlanIdMapForFilter[PLAN_MAP_KEY_3_GB],
    });
  }

  if (dataHas5GBPlan) {
    plansFilters.unshift({
      filterId: useId(),
      name: PLAN_STRING_5_GB,
      default: queryPlanValue
        ? formatSelectedFilterValueForQuery(PLAN_STRING_5_GB) === queryPlanValue
        : defaultPlanId ===
          planStore.planAllowanceToPlanIdMapForFilter[PLAN_MAP_KEY_5_GB],
    });
  }

  const downpaymentFilters = [
    {
      filterId: useId(),
      name: PARTIAL_PAYMENT_STRING_DIRECT,
      default: queryPartialPaymentValue
        ? formatSelectedFilterValueForQuery(PARTIAL_PAYMENT_STRING_DIRECT) ===
          queryPartialPaymentValue
        : defaultDownPayment === 1,
    },
    {
      filterId: useId(),
      name: PARTIAL_PAYMENT_STRING_12,
      default: queryPartialPaymentValue
        ? formatSelectedFilterValueForQuery(PARTIAL_PAYMENT_STRING_12) ===
          queryPartialPaymentValue
        : defaultDownPayment === 12,
    },
    {
      filterId: useId(),
      name: PARTIAL_PAYMENT_STRING_24,
      default: queryPartialPaymentValue
        ? formatSelectedFilterValueForQuery(PARTIAL_PAYMENT_STRING_24) ===
          queryPartialPaymentValue
        : defaultDownPayment === 24,
    },
    {
      filterId: useId(),
      name: PARTIAL_PAYMENT_STRING_36,
      default: queryPartialPaymentValue
        ? formatSelectedFilterValueForQuery(PARTIAL_PAYMENT_STRING_36) ===
          queryPartialPaymentValue
        : defaultDownPayment === 36,
    },
  ];

  const manufacturerFilters = uniqueManufacturerNames
    .map((value) => {
      return {
        filterId: useId(),
        name: value,
        isFeatured: !isUsed && value in featuredRow,
        default: queryVendorValues.includes(
          formatSelectedFilterValueForQuery(value),
        ),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const filterOptions: FilterGroup[] = [
    {
      title: 'Tillverkare',
      description: 'Välj en eller flera',
      filters: manufacturerFilters,
    },
    {
      title: 'Abonnemang',
      type: 'radio',
      description: 'Hur mycket surf behöver du?',
      filters: plansFilters,
      alternateTitle: 'Jag är student!',
    },
    {
      title: 'Delbetalning',
      description: 'Hur bekvämt vill du betala av luren?',
      type: 'radio',
      filters: downpaymentFilters,
    },
    {
      title: 'Skick',
      description: '',
      type: 'radio',
      filters: [
        {
          filterId: useId(),
          name: 'Begagnat',
          linkUrl: '/begagnat',
          default: isUsed,
        },
        {
          filterId: useId(),
          name: 'Nytt',
          linkUrl: '/mobiltelefoner',
          default: !isUsed,
        },
      ],
    },
  ];

  if (isUsed) {
    filterOptions.push({
      title: 'Begagnat skick',
      description: 'Välj en eller flera',
      filters: [
        {
          filterId: useId(),
          name: 'Perfekt skick',
          default: true,
          isFeatured: true,
          iconName: 'recycle-diamond',
        },
        {
          filterId: useId(),
          name: 'Bra skick',
          default: true,
          isFeatured: true,
          iconName: 'recycle-heart',
        },
      ],
    });
  }

  return filterOptions;
};

export const createAccessoryFilterOptions = (
  uniqueManufacturerNames: string[],
  uniqueCategoryNames: string[],
): FilterGroup[] => {
  const queryVendorValues =
    readFilterQueryValueForFilterGroup('Tillverkare').split(',');
  const queryCategoryValues =
    readFilterQueryValueForFilterGroup('Kategorier').split(',');

  const featuredRow = {
    Klockor: true,
    'Hörlurar och Ljud': true,
    Apple: true,
    Samsung: true,
    Google: true,
  };

  const manufacturerFilters = uniqueManufacturerNames
    .map((value) => {
      return {
        filterId: useId(),
        name: value,
        isFeatured: value in featuredRow,
        default: queryVendorValues.includes(
          formatSelectedFilterValueForQuery(value),
        ),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'sv-se'));

  const categoryFilters = uniqueCategoryNames
    .map((value) => {
      return {
        filterId: useId(),
        name: value,
        isFeatured: value in featuredRow,
        default: queryCategoryValues.includes(
          formatSelectedFilterValueForQuery(value),
        ),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'sv-se'));

  return [
    {
      title: 'Tillverkare',
      description: 'Välj en eller flera',
      filters: manufacturerFilters,
    },
    {
      title: 'Kategorier',
      description: 'Välj en eller flera',
      filters: categoryFilters,
    },
  ];
};
